/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useBestFitQuery } from '../../../Hooks/Api/useBestFitQuery';
import { studioTagConfig } from '../../../Config/StudioTagConfig';
import { removeSubstrings } from '../../../Services/Helper/RemoveSubstringHelper';

/**
 * Custom useStudioForm hook
 * @param context
 * @param sitePackage
 * @param studioWhitelist
 * @param resetValidSteps
 * @returns {{isLoading: boolean, studios: *[], resetContractOnStudioChange: resetContractOnStudioChange}}
 */
export function useStudioForm(context, sitePackage, studioWhitelist, resetValidSteps) {
  const { data, isLoading } = useBestFitQuery('studios', context === 'Development' ? {
    studioTags: `${studioTagConfig[sitePackage]},${studioTagConfig.SandboxStudio}`,
  } : {
    studioTags: studioTagConfig[sitePackage],
  });
  const { values, setFieldValue } = useFormikContext();
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(values.studioId);

  const resetContractOnStudioChange = (e) => {
    const selectedStudioValue = e.target.value;
    if (selectedStudio !== selectedStudioValue) {
      setSelectedStudio(values.studioId);
      setFieldValue('contract', '');
      resetValidSteps();
    }
  };

  // Set the selected studios name
  useEffect(() => {
    if (values.studioId) {
      const studioData = data.filter((studio) => studio.id === values.studioId)[0];
      setFieldValue('studioName', removeSubstrings(studioData.studioName, ['Ai Fitness', 'jumpers fitness']).trim());
      setFieldValue('studioEmail', studioData.studioEmail);
      setFieldValue('studioStreet', `${studioData.address.street} ${studioData.address.houseNumber}`);
      setFieldValue('studioPostalCode', studioData.address.zipCode);
      setFieldValue('studioLocality', studioData.address.city);
    }
  }, [data, setFieldValue, values.studioId]);

  // Transform studio data when data is fetched
  useEffect(() => {
    const filterStudios = (studioData) => {
      const isInWhitelist = !studioWhitelist
              || studioWhitelist.length === 0
              || studioWhitelist.includes(studioData.id.toString());
      return studioData.hasRateBundles === true && isInWhitelist;
    };

    const mapStudios = (bookableStudio) => ({
      label: removeSubstrings(bookableStudio.studioName, ['Ai Fitness', 'jumpers fitness']).trim(),
      value: bookableStudio.id,
    });

    if (data) {
      const filteredStudios = data.filter(filterStudios);
      const mappedStudios = filteredStudios.map(mapStudios);
      setStudios(mappedStudios);
    }
  }, [data, studioWhitelist]);

  return {
    isLoading, studios, resetContractOnStudioChange,
  };
}

useStudioForm.propTypes = {
  context: PropTypes.string.isRequired,
  sitePackage: PropTypes.string.isRequired,
  studioWhitelist: PropTypes.array.isRequired,
  resetValidSteps: PropTypes.func.isRequired,
};
